import React, { ReactNode, useState, LegacyRef } from "react";

import {
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  InputProps,
  Icon,
  Select,
  SelectProps,
  Textarea,
  TextareaProps
} from "@chakra-ui/react";

import {
  AiOutlineSearch,
  AiOutlineMail,
  AiOutlineEdit,
  AiOutlinePhone
} from "react-icons/ai";
import { MdLockOutline } from "react-icons/md";
import { BiEnvelope } from "react-icons/bi";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { ImUser } from "react-icons/im";

interface Props {
  children?: ReactNode;
  label: string;
  hideLabel?: boolean;
  register: any;
  inputIcon?: "edit" | "search" | "email" | "password" | "user" | "phone";
  type?:
    | "text"
    | "email"
    | "password"
    | "tel"
    | "number"
    | "date"
    | "time"
    | "textarea"
    | "select";
  errorMessage?: string;
  required?: boolean;
  disabled?: boolean;
  onChangeOverride?: any;
}

type TextInputProps = Props & InputProps & TextareaProps & SelectProps;
interface IconProps {
  icon: string;
}

const LeftIcon: React.FC<IconProps> = ({ icon }: IconProps) => {
  switch (icon) {
    case "search":
      return <Icon as={AiOutlineSearch} color="gray.300" w={6} h={6} />;
    case "email":
      return <Icon as={AiOutlineMail} color="gray.300" w={6} h={6} />;
    case "password":
      return <Icon as={MdLockOutline} color="gray.300" w={6} h={6} />;
    case "user":
      return <Icon as={ImUser} color="gray.300" w={6} h={6} />;
    case "edit":
      return <Icon as={AiOutlineEdit} color="gray.300" w={6} h={6} />;
    case "phone":
      return <Icon as={AiOutlinePhone} color="gray.300" w={6} h={6} />;
    default:
      return <></>;
  }
};

const TextInput: React.FC<TextInputProps> = ({
  children,
  label,
  hideLabel,
  register,
  errorMessage,
  inputIcon,
  type = "text",
  onChangeOverride,
  ...rest
}: TextInputProps) => {
  const [inputType, setInputType] = useState(type);

  const inputComponent = (): JSX.Element => {
    switch (type) {
      case "textarea":
        return (
          <Textarea
            pl={inputIcon && "3rem"}
            ref={register as LegacyRef<HTMLTextAreaElement>}
            width="100%"
            whiteSpace="pre-line"
            {...rest}
          />
        );
      case "select":
        return (
          <Select
            ref={register as LegacyRef<HTMLSelectElement>}
            width="100%"
            {...rest}
          >
            {children}
          </Select>
        );
      default:
        return (
          <>
            <Input
              type={inputType}
              pl={inputIcon && "3rem"}
              ref={register as LegacyRef<HTMLInputElement>}
              width="100%"
              onChange={
                onChangeOverride as React.ChangeEventHandler<HTMLInputElement>
              }
              {...rest}
            />
            {type === "password" && (
              <InputRightElement
                width="3rem"
                justifyContent="center"
                height="100%"
              >
                <Icon
                  as={inputType === "password" ? FaEye : FaEyeSlash}
                  color="gray.500"
                  onClick={() =>
                    setInputType(inputType === "password" ? "text" : "password")
                  }
                />
              </InputRightElement>
            )}
          </>
        );
    }
  };

  return (
    <FormControl isInvalid={!!errorMessage} width="100%">
      <FormLabel htmlFor={rest.name} fontSize="sm" hidden={hideLabel}>
        {label}
      </FormLabel>
      <InputGroup>
        {inputIcon && (
          <InputLeftElement width="3rem" justifyContent="center" height="100%">
            <LeftIcon icon={inputIcon} />
          </InputLeftElement>
        )}
        {inputComponent()}
      </InputGroup>
      <FormErrorMessage>{errorMessage && errorMessage}</FormErrorMessage>
    </FormControl>
  );
};

export default TextInput;
